import { useMemo } from 'react'
import { PostCard } from '@/components/PostCard'
import { useCuration } from '@/contexts/curation'
import { RawCurationPost } from '@/types/curation'

function estimatePostHeight(post: RawCurationPost): number {
  let height = 200

  if (post.title) {
    height += 30
  }

  const contentLength = post.content?.length || 0
  if (contentLength > 0) {
    const lines = Math.ceil(contentLength / 80)
    height += Math.min(lines * 24, 120)
  }

  const imagesLength = post.properties?.images?.length ?? 0
  const mediaLength = post.metadata?.media?.length ?? 0
  
  const hasMedia = imagesLength > 0 || 
                   (post.properties?.media_type && post.properties.media_type !== 'none') ||
                   mediaLength > 0 ||
                   (post.metadata?.media_type && post.metadata.media_type !== 'none') ||
                   !!post.properties?.media_url ||
                   !!post.metadata?.media_url ||
                   !!post.properties?.embed_url ||
                   !!post.metadata?.embed_url

  if (hasMedia) {
    const imageCount = imagesLength || mediaLength || 0
    if (imageCount > 1) {
      height += 300
    } else {
      height += 250
    }
  }

  height += 60

  return height
}

function distributePosts(posts: RawCurationPost[]): [RawCurationPost[], RawCurationPost[], RawCurationPost[]] {
  const columns: [RawCurationPost[], RawCurationPost[], RawCurationPost[]] = [[], [], []]
  const columnHeights = [0, 0, 0]

  posts.forEach((post) => {
    const estimatedHeight = estimatePostHeight(post)
    const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights))
    columns[shortestColumnIndex].push(post)
    columnHeights[shortestColumnIndex] += estimatedHeight
  })

  return columns
}

export function CurationGrid() {
  const { posts, isLoading, isFetchingMore, offset } = useCuration()

  const [leftColumnPosts, middleColumnPosts, rightColumnPosts] = useMemo(() => {
    return distributePosts(posts)
  }, [posts])

  if (isLoading && offset === 0) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[200px]">
        <div className="col-span-full flex items-center justify-center py-12 text-gray-600">
          Carregando...
        </div>
      </div>
    )
  }

  if (!isLoading && posts.length === 0) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[200px]">
        <div className="col-span-full flex items-center justify-center py-12 text-gray-600">
          Nenhum conteúdo encontrado.
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          {leftColumnPosts.map((post) => (
            <PostCard key={post.id} post={post} status="pending" />
          ))}
        </div>
        <div className="space-y-6">
          {middleColumnPosts.map((post) => (
            <PostCard key={post.id} post={post} status="pending" />
          ))}
        </div>
        <div className="space-y-6">
          {rightColumnPosts.map((post) => (
            <PostCard key={post.id} post={post} status="pending" />
          ))}
        </div>
      </div>
      {isFetchingMore && (
        <div className="col-span-full flex items-center justify-center py-6 text-gray-600 mt-6">
          Carregando...
        </div>
      )}
    </>
  )
}
